/**
 * Template Context Provider
 * Provides template-related state and operations throughout the app
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GeneratedTemplate,
  TemplateMetadata,
  JSONSchema,
  UXGenieState
} from '../../modules/shared/types/src/lib/template-types';
import { templateStorageService } from '@circuitry-ai/doc-data';
import { extractClaimDataSchema } from '../utils/extractSchema';

interface TemplateContextState {
  // Current data and schema
  currentData: any;
  currentSchema: JSONSchema | null;
  currentTemplate: GeneratedTemplate | null;

  // Available templates
  templates: GeneratedTemplate[];
  isLoadingTemplates: boolean;

  // Actions
  setCurrentData: (data: any) => void;
  setCurrentSchema: (schema: JSONSchema | null) => void;
  setCurrentTemplate: (template: GeneratedTemplate | null) => void;
  openUXGenie: (data: any, schema?: JSONSchema, returnTo?: string) => void;
  applyTemplate: (template: GeneratedTemplate) => void;
  loadTemplates: () => Promise<void>;
  saveTemplate: (template: GeneratedTemplate) => Promise<boolean>;
  deleteTemplate: (id: string) => Promise<boolean>;
  getTemplatesByAgentType: (agentType: string) => GeneratedTemplate[];
  refreshTemplates: () => Promise<void>;
}

const TemplateContext = createContext<TemplateContextState | undefined>(undefined);

interface TemplateProviderProps {
  children: ReactNode;
}

export const TemplateProvider: React.FC<TemplateProviderProps> = ({ children }) => {
  const navigate = useNavigate();

  // State
  const [currentData, setCurrentData] = useState<any>(null);
  const [currentSchema, setCurrentSchema] = useState<JSONSchema | null>(null);
  const [currentTemplate, setCurrentTemplate] = useState<GeneratedTemplate | null>(null);
  const [templates, setTemplates] = useState<GeneratedTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  /**
   * Load all templates from storage
   */
  const loadTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const loadedTemplates = await templateStorageService.getAll();
      setTemplates(loadedTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  /**
   * Refresh templates (alias for loadTemplates for clarity)
   */
  const refreshTemplates = useCallback(async () => {
    await loadTemplates();
  }, [loadTemplates]);

  /**
   * Navigate to UXGenie with data and schema
   */
  const openUXGenie = useCallback(
    (data: any, schema?: JSONSchema, returnTo?: string) => {
      // Extract schema if not provided
      const finalSchema = schema || extractClaimDataSchema(data);

      // Prepare state to pass to UXGenie
      const state: UXGenieState = {
        mode: 'template',
        data: JSON.stringify(data, null, 2),
        schema: JSON.stringify(finalSchema, null, 2),
        returnTo: returnTo || window.location.pathname
      };

      // Navigate to UXGenie
      navigate('/uxgenie', { state });
    },
    [navigate]
  );

  /**
   * Apply a template to current data
   */
  const applyTemplate = useCallback((template: GeneratedTemplate) => {
    setCurrentTemplate(template);
    console.log('Template applied:', template.metadata.name);
  }, []);

  /**
   * Save a template
   */
  const saveTemplate = useCallback(
    async (template: GeneratedTemplate): Promise<boolean> => {
      try {
        const success = await templateStorageService.save(template);
        if (success) {
          await loadTemplates(); // Refresh the list
          return true;
        }
        return false;
      } catch (error) {
        console.error('Failed to save template:', error);
        return false;
      }
    },
    [loadTemplates]
  );

  /**
   * Delete a template
   */
  const deleteTemplate = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const success = await templateStorageService.delete(id);
        if (success) {
          await loadTemplates(); // Refresh the list
          // If the deleted template was the current one, clear it
          if (currentTemplate?.metadata.id === id) {
            setCurrentTemplate(null);
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error('Failed to delete template:', error);
        return false;
      }
    },
    [loadTemplates, currentTemplate]
  );

  /**
   * Get templates filtered by agent type
   */
  const getTemplatesByAgentType = useCallback(
    (agentType: string): GeneratedTemplate[] => {
      return templates.filter(t => t.metadata.agentType === agentType);
    },
    [templates]
  );

  const value: TemplateContextState = {
    currentData,
    currentSchema,
    currentTemplate,
    templates,
    isLoadingTemplates,
    setCurrentData,
    setCurrentSchema,
    setCurrentTemplate,
    openUXGenie,
    applyTemplate,
    loadTemplates,
    saveTemplate,
    deleteTemplate,
    getTemplatesByAgentType,
    refreshTemplates
  };

  return <TemplateContext.Provider value={value}>{children}</TemplateContext.Provider>;
};

/**
 * Hook to use template context
 */
export const useTemplate = (): TemplateContextState => {
  const context = useContext(TemplateContext);
  if (context === undefined) {
    throw new Error('useTemplate must be used within a TemplateProvider');
  }
  return context;
};

/**
 * Hook to use template context with optional fallback
 */
export const useTemplateOptional = (): TemplateContextState | undefined => {
  return useContext(TemplateContext);
};

export default TemplateContext;

